import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Container, Alert, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ApiActions } from '../shared/apiActions';
import apiCall from '../shared/apiCall';
import PrintHeader from '../components/print/PrintHeader';
import PrintDayList from '../components/print/PrintDayList';
import PrintFooter from '../components/print/PrintFooter';

export default function PlanPrint() {
  const { t } = useTranslation();
  const { planId } = useParams();
  const location = useLocation();
  const [parsedData, setParsedData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Check if this is being used for sharing
  const isSharing = new URLSearchParams(location.search).get('share') === 'true';

  // Load plan details when component mounts
  useEffect(() => {
    fetchPlanDetails();
  }, [planId]);

  const fetchPlanDetails = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      // Use POST method and put planId in body
      const response = await apiCall("/api/model?action=" + ApiActions.GET_PLAN_BY_ID, { planId }, 'POST');
      
      if (response.success) {
        // Parse the data string to an object
        try {
          const parsedPlanData = JSON.parse(response.plan.data);
          
          // Process activities to ensure they have proper format
          if (parsedPlanData.days) {
            parsedPlanData.days.forEach(day => {
              if (day.activities) {
                day.activities = day.activities.map(activity => {
                  // Ensure name property exists
                  if (!activity.name && activity.title) {
                    activity.name = activity.title;
                  }
                  
                  // Ensure location is properly formatted
                  if (activity.location && typeof activity.location === 'object') {
                    activity.location = activity.location.address || JSON.stringify(activity.location);
                  }
                  
                  // Ensure any object properties that will be rendered directly are converted to strings
                  if (activity.cost && typeof activity.cost === 'object') {
                    activity.formattedCost = `${activity.cost.currency || ''} ${activity.cost.value || ''}`.trim();
                  }
                  
                  // Important: Keep original datetime strings
                  // No processing or formatting of datetime strings here
                  
                  return activity;
                });
              }
            });
          }
          
          setParsedData(parsedPlanData);
        } catch (err) {
          console.error('Error parsing plan data:', err);
          setError('Failed to parse plan data. The format may be invalid.');
        }
      } else {
        setError(response.message || 'Failed to load plan details.');
      }
    } catch (err) {
      console.error('Error fetching plan details:', err);
      setError('An error occurred while fetching the plan details.');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to trigger browser print dialog
  const handlePrint = () => {
    window.print();
  };

  // Update document title
  useEffect(() => {
    if (parsedData?.name) {
      document.title = `${parsedData.name} - ${isSharing ? t('planDetail.print.sharedItinerary') : t('planDetail.print.printItineraryTitle')}`;
    } else {
      document.title = isSharing ? `trip147 - ${t('planDetail.print.sharedItineraryTitle')}` : `trip147 - ${t('planDetail.print.printItineraryTitle')}`;
    }
  }, [parsedData, isSharing, t]);

  return (
    <div className="plan-print-page">
      {/* Print button (visible only on screen, not in print, and not in sharing mode) */}
      {!isSharing && (
        <div className="print-button-container screen-only">
          <button onClick={handlePrint} className="btn btn-primary">
            <i className="bi bi-printer me-2"></i>{t('planDetail.print.printItinerary')}
          </button>
        </div>
      )}

      <Container fluid className="py-4 print-container">
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">{t('planDetail.print.loading')}</span>
            </Spinner>
            <p className="mt-2">{t('planDetail.print.loadingPlanDetails')}</p>
          </div>
        ) : error ? (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        ) : parsedData ? (
          <>
            <PrintHeader planData={parsedData} />
            <PrintDayList days={parsedData.days} />
            <PrintFooter isSharing={isSharing} />
          </>
        ) : (
          <Alert variant="warning">{t('planDetail.print.noPlanData')}</Alert>
        )}
      </Container>
    </div>
  );
} 